// Unified number row — player selection + Lightning bonus display in one band.
import type { I18n } from '@aerofruits/i18n';
import { formatLightningMultiplierTenths } from '../game/lightning-types.js';
import type { LightningNumberSlot } from '../game/lightning-types.js';
import { LightningStrikeAnimator } from './lightning-strike.js';

export interface NumberSelectorBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface RoundLightningSnapshot {
  slots: LightningNumberSlot[];
  selected: number | null;
  dice: number[];
  matchFlags: boolean[];
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

export class LightningPanel {
  private readonly root: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly cards: Map<number, HTMLButtonElement> = new Map();
  private readonly i18n: I18n;
  private readonly strikeHost: HTMLElement;
  private strike: LightningStrikeAnimator | undefined;
  private selectedNumber: number | null = null;
  private revealedSlots: LightningNumberSlot[] = [];
  private matchFlags: boolean[] = [false, false, false];
  private disabled = false;
  private revealGen = 0;
  private onSelect: ((n: number) => void) | undefined;
  /** Authoritative display of the last successfully completed round. */
  private completedRound: RoundLightningSnapshot | null = null;

  constructor(
    root: HTMLElement,
    opts: { i18n: I18n; strikeHost?: HTMLElement; onSelect?: (n: number) => void },
  ) {
    this.root = root;
    this.i18n = opts.i18n;
    this.strikeHost = opts.strikeHost ?? root;
    this.onSelect = opts.onSelect;
    root.classList.add('lightning-panel');
    root.setAttribute('role', 'group');
    root.setAttribute('aria-label', opts.i18n.t('lnd.panel.label'));
    root.innerHTML = `<div class="lightning-panel__grid" id="lightning-panel-grid"></div>`;
    this.grid = root.querySelector('#lightning-panel-grid')!;
    for (let n = 1; n <= 6; n++) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'lightning-panel__card';
      card.dataset.number = String(n);
      card.setAttribute('aria-pressed', 'false');
      card.setAttribute('aria-label', String(n));
      card.innerHTML = `<span class="lightning-panel__num">${n}</span>
        <span class="lightning-panel__bolt" aria-hidden="true">⚡</span>
        <span class="lightning-panel__mult"></span>`;
      card.addEventListener('click', () => {
        if (!this.disabled) this.onSelect?.(n);
      });
      this.grid.appendChild(card);
      this.cards.set(n, card);
    }
    this.paint();
  }

  setOnSelect(cb: (n: number) => void): void {
    this.onSelect = cb;
  }

  setBounds(bounds: NumberSelectorBounds): void {
    this.root.style.left = `${bounds.left}px`;
    this.root.style.top = `${bounds.top}px`;
    this.root.style.width = `${bounds.width}px`;
    this.root.style.height = `${bounds.height}px`;
  }

  getCardScreenRect(n: number): DOMRect | null {
    const card = this.cards.get(n);
    if (!card) return null;
    return card.getBoundingClientRect();
  }

  captureRoundState(): RoundLightningSnapshot {
    return {
      slots: [...this.revealedSlots],
      selected: this.selectedNumber,
      dice: [],
      matchFlags: [...this.matchFlags],
    };
  }

  restoreRoundState(snapshot: RoundLightningSnapshot): void {
    this.revealedSlots = [...snapshot.slots];
    this.selectedNumber = snapshot.selected;
    this.matchFlags = [...snapshot.matchFlags];
    this.disabled = false;
    this.root.classList.remove('lightning-panel--rolling');
    this.root.classList.add('lightning-panel--revealed');
    this.applySlotClasses();
    this.paint();
  }

  setSelectedNumber(n: number | null): void {
    this.selectedNumber = n;
    this.paint();
  }

  /** Clears the previous round and disables interaction at new-round start. */
  prepareRoll(): void {
    this.disabled = true;
    this.revealedSlots = [];
    this.matchFlags = [false, false, false];
    this.revealGen += 1;
    this.strike?.cancel();
    this.root.classList.remove(
      'lightning-panel--rolling',
      'lightning-panel--revealed',
      'lightning-panel--no-lightning',
    );
    this.root.classList.add('lightning-panel--rolling');
    this.clearSlotClasses();
    this.paint();
  }

  async revealLightning(slots: LightningNumberSlot[], reducedMotion: boolean): Promise<void> {
    const gen = ++this.revealGen;
    this.revealedSlots = slots;
    this.root.classList.remove('lightning-panel--rolling');
    if (!this.strike) this.strike = new LightningStrikeAnimator(this.strikeHost);

    if (slots.length === 0) {
      this.root.classList.add('lightning-panel--no-lightning');
      this.paint();
      if (!reducedMotion) {
        this.root.classList.add('lightning-panel--pulse');
        await sleep(280);
        if (gen === this.revealGen) this.root.classList.remove('lightning-panel--pulse');
      }
      return;
    }

    for (let i = 0; i < slots.length; i++) {
      if (gen !== this.revealGen) return;
      const slot = slots[i]!;
      const rect = this.getCardScreenRect(slot.number);
      if (rect) {
        await this.strike.strikeCard(
          { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
          reducedMotion,
        );
      }
      if (gen !== this.revealGen) return;
      const card = this.cards.get(slot.number);
      card?.classList.add('lightning-panel__card--lightning', 'lightning-panel__card--struck');
      const mult = card?.querySelector('.lightning-panel__mult');
      if (mult) mult.textContent = formatLightningMultiplierTenths(slot.tenths);
      this.paint();
      if (i < slots.length - 1) await sleep(reducedMotion ? 80 : 180);
    }
    if (gen === this.revealGen) this.root.classList.add('lightning-panel--revealed');
  }

  setMatchHighlights(dice: number[], selected: number): void {
    this.matchFlags = dice.map((d) => d === selected);
    for (let n = 1; n <= 6; n++) {
      const card = this.cards.get(n)!;
      const isWin = n === selected && this.matchFlags.some(Boolean);
      card.classList.toggle('lightning-panel__card--win', isWin);
    }
    this.paint();
  }

  /** Persists the completed round for post-READY display and error recovery. */
  commitRound(slots: LightningNumberSlot[], dice: number[], selected: number): void {
    this.completedRound = {
      slots: [...slots],
      selected,
      dice: [...dice],
      matchFlags: dice.map((d) => d === selected),
    };
    this.revealedSlots = [...slots];
    this.applySlotClasses();
    this.paint();
  }

  /** Re-enables selection while keeping completed-round Lightning values visible. */
  enableAfterRound(): void {
    this.disabled = false;
    this.root.classList.remove('lightning-panel--rolling');
    if (this.revealedSlots.length > 0) this.root.classList.add('lightning-panel--revealed');
    this.paint();
  }

  /** Restores the last committed completed round (error recovery / replay exit). */
  restoreCompletedRound(): void {
    if (!this.completedRound) return;
    this.restoreRoundState(this.completedRound);
  }

  /** @deprecated Use enableAfterRound — kept for interface compatibility during migration. */
  resetIdle(): void {
    this.enableAfterRound();
  }

  cancel(): void {
    this.revealGen += 1;
    this.strike?.cancel();
    this.root.classList.remove('lightning-panel--rolling', 'lightning-panel--pulse');
    if (this.completedRound) {
      this.restoreRoundState(this.completedRound);
    } else {
      this.disabled = false;
      this.paint();
    }
  }

  destroy(): void {
    this.strike?.destroy();
    this.root.replaceChildren();
  }

  getRevealedSlots(): readonly LightningNumberSlot[] {
    return this.revealedSlots;
  }

  private applySlotClasses(): void {
    const slotMap = new Map(this.revealedSlots.map((s) => [s.number, s]));
    for (let n = 1; n <= 6; n++) {
      const card = this.cards.get(n)!;
      const slot = slotMap.get(n);
      card.classList.toggle('lightning-panel__card--lightning', Boolean(slot));
      card.classList.toggle('lightning-panel__card--struck', Boolean(slot));
      const mult = card.querySelector('.lightning-panel__mult');
      if (mult) mult.textContent = slot ? formatLightningMultiplierTenths(slot.tenths) : '';
    }
  }

  private clearSlotClasses(): void {
    for (const card of this.cards.values()) {
      card.classList.remove(
        'lightning-panel__card--lightning',
        'lightning-panel__card--win',
        'lightning-panel__card--struck',
      );
      const mult = card.querySelector('.lightning-panel__mult');
      if (mult) mult.textContent = '';
    }
  }

  private paint(): void {
    const slotMap = new Map(this.revealedSlots.map((s) => [s.number, s]));
    for (let n = 1; n <= 6; n++) {
      const card = this.cards.get(n)!;
      const isLightning = slotMap.has(n);
      const isSelected = n === this.selectedNumber;
      card.classList.toggle('lightning-panel__card--selected', isSelected && !isLightning);
      card.classList.toggle('lightning-panel__card--selected-lightning', isSelected && isLightning);
      card.classList.toggle('lightning-panel__card--lightning', isLightning);
      card.classList.toggle('lightning-panel__card--disabled', this.disabled);
      card.setAttribute('aria-pressed', String(isSelected));
      card.disabled = this.disabled;
      const mult = card.querySelector('.lightning-panel__mult');
      if (mult) {
        const slot = slotMap.get(n);
        mult.textContent = slot ? formatLightningMultiplierTenths(slot.tenths) : '';
      }
    }
  }
}
